// @ts-nocheck
import { ColumnDef } from '@tanstack/react-table';
import { Project } from '@/types';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  CheckCircle2,
  Play,
  Pause,
  Clock,
  Edit,
  MoreHorizontal,
  TicketIcon,
  PlusSquare,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { useDialogManager } from '@/hooks/useDialogManager';

import { TABLE_STYLES } from '@/lib/tableStyles';
import { cn } from '@/lib/utils';
import { SortableHeader } from '@/components/shared/SortableHeader';

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    dot: string;
    badge: string;
  }
> = {
  COMPLETED: {
    label: 'Completed',
    icon: CheckCircle2,
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20',
  },
  IN_PROGRESS: {
    label: 'In Progress',
    icon: Play,
    dot: 'bg-blue-500',
    badge: 'bg-blue-500/10 text-blue-600 border-blue-500/20',
  },
  NOT_STARTED: {
    label: 'Not Started',
    icon: Pause,
    dot: 'bg-slate-400',
    badge: 'bg-muted text-muted-foreground border-border',
  },
  PENDING: {
    label: 'Pending',
    icon: Clock,
    dot: 'bg-amber-500',
    badge: 'bg-amber-500/10 text-amber-600 border-amber-500/20',
  },
};

const ProjectStatusBadge = ({ status }: { status: string }) => {
  const c = STATUS_CONFIG[status] ?? STATUS_CONFIG.NOT_STARTED;
  const Icon = c.icon;
  return (
    <Badge
      variant="outline"
      className={cn('gap-1.5 text-xs font-medium', c.badge)}
    >
      <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', c.dot)} />
      <span>{c.label}</span>
    </Badge>
  );
};

// ─── Columns ──────────────────────────────────────────────────────────────────

export const useProjectTableColumns = () => {
  const { openDialog } = useDialogManager();
  const navigate = useNavigate();

  return useMemo<ColumnDef<Project>[]>(
    () => [
      // ── Project name ──────────────────────────────────────────────────
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <SortableHeader column={column} title="Project" />
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
              <span className="text-xs font-bold text-primary">
                {row.original.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground leading-tight">
                {row.original.name}
              </p>
              <p className="text-xs text-muted-foreground font-mono">
                {row.original.projectNumber}
              </p>
            </div>
          </div>
        ),
      },

      // ── Project type ──────────────────────────────────────────────────
      {
        accessorKey: 'type',
        header: ({ column }) => <SortableHeader column={column} title="Type" />,
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.projectType?.name ?? '—'}
          </span>
        ),
      },

      // ── Status ────────────────────────────────────────────────────────
      {
        accessorKey: 'status',
        header: ({ column }) => (
          <SortableHeader column={column} title="Status" />
        ),
        cell: ({ row }) => <ProjectStatusBadge status={row.original.status} />,
        size: 150,
      },

      // ── Tickets ───────────────────────────────────────────────────────
      {
        id: 'tickets',
        header: () => <span className={TABLE_STYLES.headerCell}>Tickets</span>,
        cell: ({ row }) => {
          const count = row.original.tickets?.length ?? 0;
          return (
            <Link
              to={`/admin/ticket-map?projectId=${row.original.id}`}
              onClick={(e) => e.stopPropagation()}
              className={TABLE_STYLES.pill(count > 0)}
            >
              <TicketIcon className="h-3 w-3" />
              {count}
              <span className="text-[10px] opacity-60">tickets</span>
            </Link>
          );
        },
        size: 140,
      },

      // ── Actions ───────────────────────────────────────────────────────
      {
        id: 'actions',
        size: 52,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  onClick={(e) => e.stopPropagation()}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem
                  onClick={() => openDialog('edit-project', row.original.id)}
                  className="gap-2 text-sm"
                >
                  <Edit className="h-3.5 w-3.5" />
                  Edit Project
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => openDialog('create-ticket', row.original.id)}
                  className="gap-2 text-sm"
                >
                  <PlusSquare className="h-3.5 w-3.5" />
                  Create Ticket
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [openDialog, navigate],
  );
};
